<template>
  <BaseModal
    :show="isOpen"
    size="sm"
    :title="t('app.views.cashRegister.closeSession') || 'Close Session'"
    :show-actions="true"
    :save-text="t('app.views.cashRegister.closeSession') || 'Close session'"
    @close="$emit('close')"
    @save="handleSave"
  >
    <div class="space-y-6">
      <!-- Session Overview -->
      <div v-if="props.report">
        <CutOverview
          :report="props.report"
          :cash="props.cash || 0"
          :card="props.card || 0"
          :digital="props.digital || 0"
          :other="props.other || 0"
          :custom-payments="props.customPayments || {}"
          :initial-balance="props.initialBalance"
        />
      </div>

      <!-- Session Closing -->
      <div class="border-t pt-4">
        <!-- Toggle for denomination counting -->
        <div class="mb-4">
          <label class="flex items-center cursor-pointer">
            <input
              v-model="localUseDenominations"
              type="checkbox"
              class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            >
            <span class="ml-2 text-sm text-gray-700 dark:text-gray-200">
              {{ t('app.views.cashRegister.useDenominationCounting') || 'Count denominations' }}
            </span>
          </label>
        </div>

        <!-- Denomination Counter -->
        <div
          v-if="localUseDenominations"
          class="mb-4"
        >
          <DenominationCounter
            v-model="localDenominations"
            @update:total="localBalance = $event"
          />
        </div>

        <!-- Manual balance input -->
        <div
          v-else
          class="mb-4"
        >
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            {{ t('app.views.cashRegister.actualBalance') || 'Actual Balance' }}
          </label>
          <input
            v-model="localBalance"
            type="number"
            step="0.01"
            class="w-full rounded-md border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
        </div>

        <label class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          {{ t('app.views.cashRegister.notes') || 'Notes' }}
        </label>
        <textarea
          v-model="localNotes"
          rows="3"
          class="w-full rounded-md border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseModal from '@/components/common/BaseModal.vue';
import DenominationCounter from '@/components/DenominationCounter.vue';
import CutOverview from './CutOverview.vue';
import type { Denominations } from '@/utils/cashRegisterHelpers';

const { t } = useI18n();

const props = defineProps<{
  isOpen: boolean;
  actualBalance: number;
  notes: string;
  useDenominations: boolean;
  denominations: Denominations;
  realCashBalance?: number;
  initialBalance?: number;
  report?: {
    total_sales: number;
    total_expenses: number;
    net_cash_flow: number;
    payment_breakdown: Record<string, number>;
  };
  cash?: number;
  card?: number;
  digital?: number;
  other?: number;
  customPayments?: Record<string, number>;
}>();



const emit = defineEmits<{
  close: [];
  save: [data: { balance: number; notes: string; useDenominations: boolean; denominations: Denominations }];
}>();

const localBalance = ref(props.actualBalance);
const localNotes = ref(props.notes);
const localUseDenominations = ref(props.useDenominations);
const localDenominations = ref(props.denominations);

watch(() => props.actualBalance, (newVal) => localBalance.value = newVal);
watch(() => props.notes, (newVal) => localNotes.value = newVal);
watch(() => props.useDenominations, (newVal) => localUseDenominations.value = newVal);
watch(() => props.denominations, (newVal) => localDenominations.value = newVal, { deep: true });

const handleSave = () => {
  emit('save', {
    balance: localBalance.value,
    notes: localNotes.value,
    useDenominations: localUseDenominations.value,
    denominations: localDenominations.value
  });
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN'
  }).format(value || 0);
};
</script>
